import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'
import { Category, Categorization } from '@/types/category'
import { Tag } from '@/types/tag'

interface CategoryFilterState {
  selectedCategories: Category[]
  preSelectedCategories: Category[]
  allCategories: Categorization[]
  isModalOpen: boolean
  filterText: string
  overSelected: boolean
}

const initialState: CategoryFilterState = {
  selectedCategories: [],
  preSelectedCategories: [],
  allCategories: [],
  isModalOpen: false,
  filterText: '',
  overSelected: false,
}

export const categoryFilterSlice = createSlice({
  name: 'categoryFilter',
  initialState,
  reducers: {
    setAllCategories: (state, action: PayloadAction<Categorization[]>) => {
      state.allCategories = action.payload
    },
    setSelectedCategories: (state, action: PayloadAction<Category[]>) => {
      state.selectedCategories = action.payload
    },
    setPreSelectedCategories: (state, action: PayloadAction<Category[]>) => {
      state.preSelectedCategories = action.payload
    },
    toggleCategorySelection: (state, action: PayloadAction<Category | Tag>) => {
      const category = action.payload as Category
      const existingIndex = state.selectedCategories.findIndex((c) => c.id === category.id)

      if (existingIndex >= 0) {
        // Remove if already selected
        state.selectedCategories.splice(existingIndex, 1)
        state.overSelected = false
      } else {
        // Add if not selected and under limit
        if (state.selectedCategories.length < 3) {
          state.selectedCategories.push(category)
          state.overSelected = false
        } else {
          state.overSelected = true
        }
      }
    },
    removeAllSelectedCategories: (state) => {
      state.selectedCategories = []
      state.overSelected = false
    },
    setModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload
    },
    setFilterText: (state, action: PayloadAction<string>) => {
      state.filterText = action.payload
    },
    setOverSelected: (state, action: PayloadAction<boolean>) => {
      state.overSelected = action.payload
    },
    resetToPreSelected: (state) => {
      state.selectedCategories = [...state.preSelectedCategories]
      state.overSelected = false
    },
    applySelectedCategories: (state) => {
      state.preSelectedCategories = [...state.selectedCategories]
    },
  },
})

export const {
  setAllCategories,
  setSelectedCategories,
  setPreSelectedCategories,
  toggleCategorySelection,
  removeAllSelectedCategories,
  setModalOpen,
  setFilterText,
  setOverSelected,
  resetToPreSelected,
  applySelectedCategories,
} = categoryFilterSlice.actions

// Selectors
export const selectAllCategories = (state: RootState): Categorization[] =>
  state.categoryFilter.allCategories

export const selectSelectedCategories = (state: RootState): Category[] =>
  state.categoryFilter.selectedCategories

export const selectPreSelectedCategories = (state: RootState): Category[] =>
  state.categoryFilter.preSelectedCategories

export const selectIsModalOpen = (state: RootState): boolean => state.categoryFilter.isModalOpen

export const selectFilterText = (state: RootState): string => state.categoryFilter.filterText

export const selectOverSelected = (state: RootState): boolean => state.categoryFilter.overSelected

export const selectFilteredCategories = (state: RootState): Categorization[] => {
  const { allCategories, filterText } = state.categoryFilter

  if (!filterText.trim()) {
    return allCategories
  }

  return allCategories
    .map((categoryGroup) => ({
      ...categoryGroup,
      records: categoryGroup.records.filter((category) =>
        category.name.toLowerCase().includes(filterText.toLowerCase()),
      ),
    }))
    .filter((categoryGroup) => categoryGroup.records.length > 0)
}

export const selectSelectedCategoryNames = (state: RootState): string => {
  const preSelected = state.categoryFilter.preSelectedCategories
  return preSelected.length > 0 ? preSelected.map((c) => c.name).join(', ') : '選択されていません'
}

export const selectIsSubmitDisabled = (state: RootState): boolean => {
  const { selectedCategories, preSelectedCategories } = state.categoryFilter

  if (selectedCategories.length !== preSelectedCategories.length) {
    return false
  }

  const selectedIds = selectedCategories.map((c) => c.id).sort()
  const preSelectedIds = preSelectedCategories.map((c) => c.id).sort()

  return selectedIds.every((id, index) => id === preSelectedIds[index])
}

export default categoryFilterSlice.reducer
